import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import PracticeMode from './components/PracticeMode';
import ForecastMode from './components/ForecastMode';
import MCQMode from './components/MCQMode';
import StyleAdaptiveMode from './components/StyleAdaptiveMode';
import Header from './components/Header';
import ContactPage from './components/ContactPage';
import DeveloperPage from './components/DeveloperPage';
import './App.css';

// API base URL from environment variable
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';

function App() {
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedFireworkModel, setSelectedFireworkModel] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('mcq');
  
  // Add state for persisting questions across tab switches
  const [mcqQuestions, setMcqQuestions] = useState(null);
  const [mcqSessionActive, setMcqSessionActive] = useState(false);
  
  const [practiceQuestions, setPracticeQuestions] = useState(null);
  const [practiceSessionActive, setPracticeSessionActive] = useState(false);
  
  const [forecastResults, setForecastResults] = useState(null);
  
  const [styleAdaptiveData, setStyleAdaptiveData] = useState(null);
  const [styleSessionActive, setStyleSessionActive] = useState(false);

  // Fetch available models on component mount
  useEffect(() => {
    fetchModels();
  }, []);

  const fetchModels = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/models`);
      const data = await response.json();
      
      if (data.models && data.models.length > 0) {
        setModels(data.models);
        
        // Set default selected model if available
        if (data.default_provider) {
          setSelectedModel(data.default_provider);
        } else {
          setSelectedModel(data.models[0].id);
        }
        
        // Set default Firework model if available
        if (data.default_firework_model) {
          setSelectedFireworkModel(data.default_firework_model);
        }
      } else {
        setError('No AI models available. Please check backend configuration.');
      }
    } catch (err) {
      setError('Failed to fetch AI models. Please check if the backend is running.');
      console.error('Error fetching models:', err);
    }
  };

  const handleModelSelect = (modelId) => {
    setSelectedModel(modelId);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  // Main page content component
  const MainPage = () => (
    <>
      <div className="text-center mb-5">
        <h1 className="display-4 fw-bold">Test-Forecast</h1>
        <p className="lead">AI-powered exam preparation and question forecasting</p>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <ul className="nav nav-pills nav-fill mb-4">
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'style-adaptive' ? 'active' : ''}`}
            onClick={() => handleTabChange('style-adaptive')}
          >
            Professor Style
          </button>
        </li>
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'mcq' ? 'active' : ''}`}
            onClick={() => handleTabChange('mcq')}
          >
            MCQ Mode
          </button>
        </li>
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'practice' ? 'active' : ''}`}
            onClick={() => handleTabChange('practice')}
          >
            Gen Practice Mode
          </button>
        </li>
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'forecast' ? 'active' : ''}`}
            onClick={() => handleTabChange('forecast')}
          >
            Forecast Mode
          </button>
        </li>
      </ul>

      {activeTab === 'practice' && (
        <PracticeMode 
          models={models}
          selectedModel={selectedModel}
          onModelSelect={handleModelSelect}
          apiBaseUrl={API_BASE_URL}
          selectedFireworkModel={selectedFireworkModel}
          setSelectedFireworkModel={setSelectedFireworkModel}
          questions={practiceQuestions}
          setQuestions={setPracticeQuestions}
          sessionActive={practiceSessionActive}
          setSessionActive={setPracticeSessionActive}
        />
      )}
      {activeTab === 'style-adaptive' && (
        <StyleAdaptiveMode 
          models={models}
          selectedModel={selectedModel}
          onModelSelect={handleModelSelect}
          apiBaseUrl={API_BASE_URL}
          selectedFireworkModel={selectedFireworkModel}
          setSelectedFireworkModel={setSelectedFireworkModel}
          styleData={styleAdaptiveData}
          setStyleData={setStyleAdaptiveData}
          sessionActive={styleSessionActive}
          setSessionActive={setStyleSessionActive}
        />
      )}
      {activeTab === 'mcq' && (
        <MCQMode 
          models={models}
          selectedModel={selectedModel}
          onModelSelect={handleModelSelect}
          apiBaseUrl={API_BASE_URL}
          selectedFireworkModel={selectedFireworkModel}
          setSelectedFireworkModel={setSelectedFireworkModel}
          questions={mcqQuestions}
          setQuestions={setMcqQuestions}
          sessionActive={mcqSessionActive}
          setSessionActive={setMcqSessionActive}
        />
      )}
      {activeTab === 'forecast' && (
        <ForecastMode 
          models={models}
          selectedModel={selectedModel}
          onModelSelect={handleModelSelect}
          apiBaseUrl={API_BASE_URL}
          selectedFireworkModel={selectedFireworkModel}
          setSelectedFireworkModel={setSelectedFireworkModel}
          forecast={forecastResults}
          setForecast={setForecastResults}
        />
      )}
    </>
  );

  return (
    <Router>
      <Header />
      <div className="container py-4">
        <Routes>
          <Route path="/" element={<MainPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/developer" element={<DeveloperPage />} />
        </Routes>
        
        <footer className="mt-5 text-center text-muted">
          <p>&copy; {new Date().getFullYear()} Test-Forecast. All rights reserved.</p>
        </footer>
      </div>
    </Router>
  );
}

export default App;